import '../scss/components/search.scss';
import { dlv } from './utils/dlv.js';
import { createFacetFilter, removeFilters } from './utils/filter.js';
import { ensureArray } from './utils/ensure-array.js';
import {
    initLightbox,
    addItemToLightbox,
    deleteItemFromLightbox,
    deleteItemFromLocalStorage,
    isItemInLightbox
} from "./utils/lightbox.js";
import {
    searchObjectDefault,
    getSearchObject,
    setSearchObject,
    updateSearchUrl,
    getSearchParams
} from './utils/search-params.js';

const searchEl = document.querySelector('.search-aps');
const searchMainEl = searchEl.querySelector('.search_main');
const searchSidebar = searchEl.querySelector('.search_sidebar');
const searchResultsContainer = searchEl.querySelector('.search_results');
const notFoundEl = searchEl.querySelector('.search_no-results');
const searchMetaSortCountEl = searchMainEl.querySelector('.search_meta-sort_count');

const facetMapping = {
    'ra_facet_fotograf': 'Fotografinnen und Fotografen',
    'ra_facet_doctype': 'Sammlungen',
    'ra_facet_ort': 'Ort',
    'ra_facet_thema': 'Thema',
    'ra_facet_personen': 'Personen',
    'ra_facet_technik': 'Technik',
}

let searchResults = {};
let searchConfig = {
    query: '',
    pageLower: 0,
    page: 0,
    pageUpper: 1,
    offset: 0,
    size: 20,
    sortBy: 'relevance',
    sortDir: 'desc',
    requiredfields: null
};
let isLoading = false;
let initialPage = 0
const loadingSignals = {
    lastLoading: null,
    hasLessItems: false,
    hasMoreItems: false,
}
let abortController = null; // Controller für Request-Abbruch
const searchResultDefault = {
    count: 0,
    totalCount: 0,
    results: [],
    facets: [],
    hasLess: false,
    hasMore: false,
    query: null,
};

const loadMoreObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
        if (entry.isIntersecting) {
            entry.target.click();
        }
    });
}, {
    root: null,
    rootMargin: '0px',
    threshold: 1.0
});

if (searchEl) {
    initLightbox();
    parseQueryParameter(getSearchParams());
    initialPage = searchConfig.page;

    await initializeSearchPage();

    // EventListeners für Meta-Buttons
    setupMetaButtons();
    setupSortButtons();
    setupLoadMoreButtons();

    // EventListener für Filter-Updates
    setupSearchFilterListener();
}

async function initializeSearchPage() {
    if (!searchConfig.query || searchConfig.query.trim() === '') {
        notFoundEl.classList.remove('d-none');
        return;
    }

    try {
        const response = await initSearch();
        if (!response) {
            throw new Error('No search response received');
        }

        const hasResults = response.totalCount > 0;

        if (response.facets) {
            createFilters(response.facets);
        }

        if (response.results) {
            createLocalStorage(response.results);
        }

        await createSearchPage(response);

        if (hasResults) {
            searchResultsContainer.classList.remove('d-none');
            updateLoadingSections();
            await setupLoadMoreObserver();
        }
    } catch (error) {
        console.error('initializeSearchPage: Failed to initialize search', error);
        notFoundEl.classList.remove('d-none');
        removePageLoadingSpinner();
        enableFilterButtons();
    }
}

async function setupLoadMoreObserver() {
    let attempts = 0;
    const maxAttempts = 25; // max 2.5 Sekunden (25 * 100ms)

    return new Promise((resolve) => {
        const checkInterval = setInterval(() => {
            const afterButton = searchMainEl.querySelector('.search_more-results button[data-action="after"]');

            if (afterButton && loadMoreObserver && loadingSignals.hasMoreItems) {
                loadMoreObserver.observe(afterButton);
                clearInterval(checkInterval);
                resolve();
            }

            attempts++;
            if (attempts >= maxAttempts) {
                clearInterval(checkInterval);
                console.warn('setupLoadMoreObserver: Button not found after initialization');
                resolve();
            }
        }, 100);
    });
}

function setupMetaButtons() {
    const allMetaOpenBtns = searchMainEl.querySelectorAll('.search_mobile-meta-sort .btn-open');
    allMetaOpenBtns.forEach((btn) => {
        btn.addEventListener('click', (event) => {
            const dataMeta = event.currentTarget.dataset.meta;
            const container = document.querySelector(`.search_mobile-overlay.${dataMeta}`);
            if (container) {
                container.classList.add('active');
            }
        });
    });

    const allMetaCloseBtns = searchMainEl.querySelectorAll('.search_mobile-meta-sort .btn-close');
    allMetaCloseBtns.forEach((btn) => {
        btn.addEventListener('click', (event) => {
            const dataMeta = event.currentTarget.dataset.meta;
            const container = document.querySelector(`.search_mobile-overlay.${dataMeta}`);
            if (container) {
                container.classList.remove('active');
            }
        });
    });
}

function setupSortButtons() {
    const allSortBtns = searchMainEl.querySelectorAll('.search_meta-sort_btn-group .button');
    allSortBtns.forEach((btn) => {
        btn.addEventListener('click', async (event) => {
            const dataSort = event.currentTarget.dataset.sort;

            if (searchConfig.sort === dataSort) {
                return; // Keine Änderung nötig
            }

            searchConfig.sort = dataSort;

            // Update UI
            allSortBtns.forEach((btn) => btn.classList.remove('active'));
            searchMainEl.querySelectorAll(`.search_meta-sort_btn-group .button[data-sort="${dataSort}"]`)
                .forEach((btn) => btn.classList.add('active'));

            updateSearchUrl({ key: 'sort', value: dataSort }, 'apsSearchFilter', 'resetPage');
        });
    });
}

function setupLoadMoreButtons() {
    const loadResultsButtons = searchMainEl.querySelectorAll('.search_more-results button');
    loadResultsButtons.forEach((btn) => {
        btn.addEventListener('click', async (event) => {
            const currentButton = event.currentTarget;
            const dataPage = Number(currentButton.dataset.page);
            loadingSignals.lastLoading = currentButton.dataset.action;

            currentButton.disabled = true;
            currentButton.classList.remove('d-none');
            currentButton.querySelector('span').style.display = 'none';
            currentButton.querySelector('svg').style.display = 'block';

            searchConfig.pageLower = dataPage !== 0 ? dataPage - 1 : 0;
            searchConfig.page = dataPage;
            searchConfig.pageUpper = dataPage + 1;
            searchConfig.offset = dataPage * searchConfig.size;
            searchConfig.from = dataPage !== 0 ? searchConfig.offset : 0;

            setSearchObject(searchConfig);
            const response = await initSearch();

            if (response) {
                if (response.results) {
                    createLocalStorage(response.results);
                }

                await createSearchPage(response);
                updateLoadingSections()
            }

            setTimeout(() => {
                currentButton.disabled = false;
                currentButton.querySelector('span').style.display = 'block';
                currentButton.querySelector('svg').style.display = 'none';
            }, 300);
        });
    });
}

function setupSearchFilterListener() {
    document.addEventListener('apsSearchFilter', async function (e) {
        // Validierung des Events
        if (!e.detail || typeof e.detail !== 'object') {
            console.error('Invalid event detail in apsSearchFilter');
            return;
        }

        if (e.detail.updated) {
            try {
                // Stop observing load more button temporarily to avoid double fetch
                const afterButton = searchMainEl.querySelector('.search_more-results button[data-action="after"]');
                if (afterButton && loadMoreObserver) {
                    loadMoreObserver.unobserve(afterButton);
                }

                // Reset pagination wenn nötig
                if (e.detail.resetFrom) {
                    searchConfig.page = 0;
                    searchConfig.from = 0;
                    searchConfig.offset = 0;

                    loadingSignals.lastLoading = null;
                    loadingSignals.hasLessItems = false;
                    loadingSignals.hasMoreItems = false;
                }

                // Parse query parameters
                parseQueryParameter(getSearchParams());

                // Fetch new search results
                const mappedSearchResponse = await initSearch();

                // Clean up existing results
                removeResults();
                removeFilters('.filter-facets_facet');

                if (mappedSearchResponse) {
                    await createSearchPage(mappedSearchResponse);
                    updateLoadingSections()

                    if (mappedSearchResponse.facets) {
                        createFilters(mappedSearchResponse.facets);
                    }

                    if (mappedSearchResponse.results) {
                        createLocalStorage(mappedSearchResponse.results);
                    }
                }

                // Re-observe load more button
                if (loadingSignals.hasMoreItems) {
                    await setupLoadMoreObserver();
                }

            } catch (error) {
                console.error('Error in apsSearchFilter event handler:', error);
                enableFilterButtons();
                removePageLoadingSpinner();
            }
        }
    });
}

function parseQueryParameter(query) {
    if (!query) return null;

    searchConfig.query = query.get('q') || '';
    searchConfig.page = query.has('page') ? Number(query.get('page') - 1) : 0;
    searchConfig.pageLower = searchConfig.page > 0 ? searchConfig.page - 1 : 0;
    searchConfig.pageUpper = searchConfig.page + 1;
    searchConfig.size = query.get('size') || 20;
    searchConfig.offset = searchConfig.page < 1 ? 0 : searchConfig.size * searchConfig.page;
    searchConfig.sort = query.get('sort') || 'relevance';
    searchConfig.sortDir = query.get('sortDir') || (searchConfig.sort === 'relevance' ? 'desc' : 'asc');
    searchConfig.requiredfields = query.get('requiredfields');

    const dataSort = searchConfig.sort !== 'az' ? 'relevance' : 'az';
    const sortButtons = searchMainEl.querySelectorAll(`.search_meta-sort_btn-group .button[data-sort="${dataSort}"]`);
    if (sortButtons) {
        sortButtons.forEach((btn) => btn.classList.add('active'));
    }

    setSearchObject(searchConfig);
}

function updateLoadingSections() {
    const loadingBeforeExisting = searchMainEl.querySelector('.search_more-results.before');
    const loadingAfterExisting = searchMainEl.querySelector('.search_more-results.after');

    if (loadingSignals.lastLoading === 'before' || loadingSignals.lastLoading === null) {
        if(loadingSignals.hasLessItems) {
            loadingBeforeExisting.querySelector('button[data-page]').dataset.page = searchConfig.pageLower;
            loadingBeforeExisting.classList.remove('d-none');
        } else {
            loadingBeforeExisting.querySelector('button[data-page]').dataset.page = '';
            loadingBeforeExisting.classList.add('d-none');
        }
    }

    if (loadingSignals.lastLoading === 'after' || loadingSignals.lastLoading === null) {
        if(loadingSignals.hasMoreItems) {
            loadingAfterExisting.querySelector('button[data-page]').dataset.page = searchConfig.pageUpper;
            loadingAfterExisting.classList.remove('d-none');
        } else {
            loadingAfterExisting.querySelector('button[data-page]').dataset.page = '';
            loadingAfterExisting.classList.add('d-none');
        }
    }
}

async function initSearch() {
    try {
        const searchObject = searchConfig.query.length !== 0 ? getSearchObject() : searchObjectDefault;

        // Validiere searchObject
        if (!searchObject || typeof searchObject !== 'object') {
            throw new Error('Invalid search object');
        }

        const searchResponse = await fetchApsData(searchObject);

        // Prüfe ob Response gültig ist
        if (!searchResponse) {
            throw new Error('Empty response from fetchApsData');
        }

        return mapSearchResponse(searchResponse);

    } catch (error) {
        console.error('initSearch: Failed to fetch or map search results', error);
    }
}

async function fetchApsData(data) {
    // Breche vorherigen Request ab falls noch aktiv
    if (abortController) {
        abortController.abort();
    }

    // Erstelle neuen AbortController
    abortController = new AbortController();

    // Validierung
    if (!data || typeof data !== 'object') {
        throw new Error('fetchApsData: Invalid data parameter');
    }

    // Verhindere mehrfaches gleichzeitiges Laden
    if (isLoading) {
        console.warn('fetchApsData: Request already in progress');
        return null;
    }

    createPageLoadingSpinner();
    disableFilterButtons();

    try {
        const response = await fetch('/api/aps/document/search', {
            method: 'POST',
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(data),
            signal: abortController.signal, // Ermöglicht Abbruch
        });

        // Prüfe HTTP Status
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const jsonData = await response.json();

        // Validiere Response-Struktur
        if (!jsonData || typeof jsonData !== 'object') {
            throw new Error('Invalid JSON response structure');
        }

        return jsonData;

    } catch (error) {
        // Ignoriere AbortError (absichtlicher Abbruch)
        if (error.name === 'AbortError') {
            // console.log('fetchApsData: Request aborted');
            return null;
        }

        // Spezifische Fehlerbehandlung
        if (error instanceof TypeError && error.message.includes('fetch')) {
            console.error('fetchApsData: Network error - check connection', error);
        } else if (error instanceof SyntaxError) {
            console.error('fetchApsData: Invalid JSON response', error);
        } else {
            console.error('fetchApsData: Failed to fetch search results', error);
        }

        // Werfe Fehler weiter für Behandlung in initSearch
        throw error;

    } finally {
        // Cleanup wird immer ausgeführt
        removePageLoadingSpinner();
        enableFilterButtons();
        abortController = null;
    }
}

function mapSearchResponse(response) {
    let searchResponse = { ...searchResultDefault };
    let resultsEntries = [];
    let adjustedSize = searchConfig.size;

    for (let result of ensureArray(response.documents.document)) {
        if (!result.docType || !result.attachment?.id) {
            adjustedSize--;
            continue;
        }
        resultsEntries.push({
            id: result.docNumber,
            docType: result.docType,
            docNumber: result.docNumber,
            title: result.title,
            archive: result.archive,
            creator: result.creator,
            attachment: {
                id: result.attachment.id,
                src: result.attachment.thumbnail,
            },
            score: result.score,
            meta: result.metaData[result.docType]
        });
    }

    searchResponse.results = resultsEntries;
    searchResponse.facets = response.documents.facets?.facet
        ? ensureArray(response.documents.facets.facet).map(facet => ({
            id: facet.name,
            name: facetMapping[facet.name],
            entries: ensureArray(facet.descriptor).map(d => ({
                id: `${facet.name}:${encodeURIComponent(d.value)}`,
                value: d.value,
                count: d.hits,
            })),
        }))
        : [];

    searchResponse.query = response.documents.query || null;
    searchResponse.count = resultsEntries.length || 0;
    searchResponse.totalCount = response.documents.results || 0;
    searchResponse.totalPage = Math.ceil(searchResponse.totalCount / searchConfig.size);
    searchResponse.hasLess = initialPage !== 0 && searchConfig.page !== 0;
    searchResponse.hasMore = searchConfig.pageUpper < searchResponse.totalPage;

    loadingSignals.hasLessItems = searchResponse.hasLess;
    loadingSignals.hasMoreItems =  searchResponse.hasMore;

    if (searchResponse.facets.length > 0) notFoundEl.classList.add('d-none');

    return searchResponse;
}

function createLocalStorage(items) {
    // Validierung der Eingabe
    if (!items || !Array.isArray(items)) {
        console.warn('createLocalStorage: Invalid items parameter, expected array');
        return false;
    }

    const lsApsSearch = {
        url: document.location.href,
        items: items.map((item) => ({
            attachmentId: item.attachment.id,
            docNumber: item.docNumber,
            url: `/dokument/${item.docNumber}/${item.attachment.id}`
        }))
    };
    localStorage.setItem('aps_search', JSON.stringify(lsApsSearch));
}

async function createSearchPage(response) {
    // Validierung der Response
    if (!response || typeof response !== 'object') {
        console.error('createSearchPage: Invalid response object');
        return;
    }

    const scrollPos = window.scrollY;
    const currentPage = searchConfig.page > 0 ? searchConfig.page : 0;
    const resultCount = response.totalCount || 0;
    const hasResults = Boolean(
        response.results &&
        Array.isArray(response.results) &&
        response.results.length > 0 &&
        resultCount > 0
    );

    try {
        if (!hasResults) {
            // Zeige "Keine Ergebnisse" Meldung
            const notFoundNode = document.getElementById('search_no-results');
            const notFoundSearchTerm = document.getElementById('search-term');

            if (notFoundNode) {
                notFoundNode.classList.remove('d-none');
            }

            if (notFoundSearchTerm && searchConfig.query) {
                notFoundSearchTerm.textContent = searchConfig.query;
            }

            // Verstecke "Mehr laden" Buttons
            document.querySelectorAll('.search_more-results').forEach(el => {
                el.classList.add('d-none');
            });
        } else {
            // Speichere Ergebnisse
            searchResults[currentPage] = response.results;

            // Füge Suchergebnisse ein
            await insertSearchResults();

            // Scroll-Position wiederherstellen
            await restoreScrollPosition(scrollPos);
        }

        // Update Ergebnis-Counter
        updateResultCount(resultCount);

        // Markiere als geladen
        searchMainEl.classList.add('loaded');

    } catch (error) {
        console.error('createSearchPage: Error creating search page', error);

        // Zeige Fehler-Nachricht
        const notFoundNode = document.getElementById('search_no-results');
        if (notFoundNode) {
            notFoundNode.classList.remove('d-none');
        }

        // Cleanup
        searchMainEl.classList.add('loaded');
    }
}

async function restoreScrollPosition(scrollPos) {
    if (typeof scrollPos !== 'number' || scrollPos < 0) {
        return;
    }

    try {
        const root = document.documentElement;
        const originalScrollBehavior = root.style.scrollBehavior;

        // Deaktiviere smooth scrolling temporär
        root.style.scrollBehavior = 'auto';

        // Verwende requestAnimationFrame für präzises Timing
        await new Promise(resolve => {
            requestAnimationFrame(() => {
                window.scrollTo({
                    left: 0,
                    top: scrollPos,
                    behavior: 'auto'
                });

                // Stelle ursprünglichen Wert wieder her
                if (originalScrollBehavior) {
                    root.style.scrollBehavior = originalScrollBehavior;
                } else {
                    root.style.removeProperty('scroll-behavior');
                }

                resolve();
            });
        });
    } catch (error) {
        console.error('restoreScrollPosition: Failed to restore scroll position', error);
    }
}

function updateResultCount(resultCount) {
    if (!searchMetaSortCountEl) {
        return;
    }

    if (resultCount > 0) {
        const formattedCount = resultCount.toLocaleString('de-DE');
        searchMetaSortCountEl.textContent = `Ungefähr ${formattedCount} Ergebnisse`;
        searchMetaSortCountEl.classList.remove('d-none');
    } else {
        searchMetaSortCountEl.classList.add('d-none');
        searchMetaSortCountEl.textContent = '';
    }
}

async function insertSearchResults() {
    return new Promise((resolveOuter, rejectOuter) => {
        const pageResults = searchResults?.[searchConfig.page];
        if (!Array.isArray(pageResults) || pageResults.length === 0) {
            return resolveOuter();
        }

        // Container sichern/verwenden
        let searchResultsRow = searchMainEl.querySelector('.search_results_row');
        if (!searchResultsRow) {
            searchResultsRow = document.createElement('div');
            searchResultsRow.classList.add('search_results_row');
            searchResultsContainer.appendChild(searchResultsRow);
        }

        const pageMarker = document.createElement('div');
        pageMarker.classList.add('search_results_page-marker');
        pageMarker.innerHTML = `<p>Seite ${searchConfig.page + 1}</p>`;

        const imagePromises = pageResults.map((result) => {
            return new Promise((resolve) => {
                const tile = document.createElement('div');
                const docNum = dlv(result, 'id');
                const title = dlv(result, 'title');
                const creator = dlv(result, 'creator');
                const archive = dlv(result, 'archive');
                const attachment = dlv(result, 'attachment.id');
                const attachmentURL = dlv(result, 'attachment.src');
                const url = `/dokument/${docNum}/${attachment}`;
                const isInLightbox = isItemInLightbox(docNum, attachment);

                const img = new Image();
                img.onload = function() {
                    const svgPath = {
                        plus: 'M368 224H224V80c0-8.84-7.16-16-16-16h-32c-8.84 0-16 7.16-16 16v144H16c-8.84 0-16 7.16-16 16v32c0 8.84 7.16 16 16 16h144v144c0 8.84 0 16 16 16h32c8.84 0 16-7.16 16-16V288h144c8.84 0 16-7.16 16-16v-32c0-8.84-7.16-16-16-16z',
                        minus: 'M368 224H16c-8.84 0-16 7.16-16 16v32c0 8.84 7.16 16 16 16h352c8.84 0 16-7.16 16-16v-32c0-8.84-7.16-16-16-16z'
                    };

                    tile.classList.add('search_results_item');
                    tile.setAttribute('style', `aspect-ratio: ${img.naturalWidth} / ${img.naturalHeight}`);
                    tile.innerHTML = `
                        <a class="search_results_item_overlay" href="${url}" title="${title || creator || archive}">
                            <div class="search_results_item_overlay-bg"></div>
                            ${title ? `<p class="bold">${title}</p>` : ''}
                            <p>${creator || archive}</p>
                            <button class="button" type="button" data-action="${!isInLightbox ? 'add' : 'remove'}"
                            title="${!isInLightbox ? 'Auf den Leuchttisch legen' : 'Vom Leuchttisch entfernen'}">
                            <svg focusable="false" viewBox="0 0 384 512" aria-hidden="true">
                                <path d="${!isInLightbox ? svgPath.plus : svgPath.minus}" style="fill: currentcolor;"></path>
                            </svg>
                            </button>
                        </a>
                        <img src="${attachmentURL}" height="${img.naturalHeight}" width="${img.naturalWidth}" style="aspect-ratio: ${img.naturalWidth} / ${img.naturalHeight}" alt="${title || creator || archive}" />
                    `;

                    tile.querySelector('button').addEventListener('click', (e) => {
                        e.preventDefault();
                        const targetButton = e.currentTarget;
                        const action = targetButton.dataset.action;

                        if (action === 'add') {
                            addItemToLightbox({
                                apsImageId: docNum,
                                attachment: attachment,
                                archive: archive,
                                creator: creator,
                                title: title,
                                thumbnail: attachmentURL,
                            }).then(response => {
                                if (response === 200) {
                                    targetButton.dataset.action = 'remove';
                                    targetButton.title = 'Vom Leuchttisch entfernen';
                                    targetButton.querySelector('path').setAttribute('d', svgPath.minus);
                                }
                            });
                        } else {
                            deleteItemFromLightbox(docNum, attachment).then(response => {
                                if (response === 200) {
                                    targetButton.dataset.action = 'add';
                                    targetButton.title = 'Auf den Leuchttisch legen';
                                    targetButton.querySelector('path').setAttribute('d', svgPath.plus);
                                }
                            }).catch(error => {
                                if (error.toString().includes('404')) {
                                    targetButton.dataset.action = 'add';
                                    targetButton.title = 'Auf den Leuchttisch legen';
                                    targetButton.querySelector('path').setAttribute('d', svgPath.plus);
                                    deleteItemFromLocalStorage(docNum, attachment);
                                }
                            });
                        }
                    });

                    resolve(tile);
                };
                img.onerror = function(e) {
                    console.error('Image failed to load:', attachmentURL, e);
                    resolve(tile);
                };
                img.src = attachmentURL;
            });
        });

        Promise.all(imagePromises).then((tiles) => {
            const fragment = document.createDocumentFragment();

            if (loadingSignals.lastLoading === 'after' || loadingSignals.lastLoading === null) {
                fragment.appendChild(pageMarker);
                tiles.forEach(tile => fragment.appendChild(tile));
                searchResultsRow.appendChild(fragment);
            } else {
                fragment.append(...tiles);
                fragment.insertBefore(pageMarker, fragment.firstChild);
                searchResultsRow.insertBefore(fragment, searchResultsRow.firstChild);
            }

            resolveOuter();
        }).catch(rejectOuter);
    });
}

function createFilters(response) {
    if (!response || !Array.isArray(response)) {
        console.warn('Invalid facets response for createFilters');
        return;
    }

    try {
        const facets = splitFacets(response, searchConfig.requiredfields);
        if (facets && facets.length > 0) {
            createFacetFilter(facets, '.search_filter.filter-facets', 'apsSearchFilter');
        }
    } catch (error) {
        console.error('Error creating filters:', error);
    }
}

function splitFacets(responseFacets, requiredfields) {
    const requiredfieldsObject = parseRequiredFields(requiredfields);
    const newFacets = [];

    if (!responseFacets) return newFacets;

    const noFacets = searchSidebar.querySelector('.filter-facets_no-facets');
    if (noFacets) noFacets.remove();

    responseFacets.forEach(facet => {
        if (facet.entries && facet.entries.length) {
            const filtered = {
                selectedEntries: [],
                otherEntries: [],
                moreNum: 0,
            };

            for (const entry of facet.entries) {
                if (requiredfieldsObject && requiredfieldsObject[facet.id]) {
                    const isValueIncluded = requiredfieldsObject[facet.id].some(value => entry.value === value);

                    if (isValueIncluded) {
                        filtered.selectedEntries.push(entry);
                    } else {
                        filtered.otherEntries.push(entry);
                        filtered.moreNum += 1;
                    }
                } else {
                    filtered.otherEntries.push(entry);
                    filtered.moreNum += 1;
                }
            }

            newFacets.push({
                ...facet,
                ...filtered,
            });
        }
    });

    return newFacets;
}

function removeResults() {
    const container = searchMainEl.querySelector('.search_results_row');
    if (container) {
        container.remove();
    }
    searchResults = {};
}

function parseRequiredFields(inputString) {
    if (!inputString) return null;
    const resultObject = {};
    const entries = inputString.split('|');

    entries.forEach((entry, index) => {
        const pairs = entry.split('"."');

        pairs.forEach(pair => {
            const escapedPair = pair.replace(/"/g, '');
            const [field, value] = escapedPair.split(':');
            const unescapedField = decodeURIComponent(field.trim());
            const unescapedValue = decodeURIComponent(value.trim());

            if (!resultObject[unescapedField]) {
                resultObject[unescapedField] = [];
            }

            resultObject[unescapedField].push(unescapedValue);
        });
    });

    return resultObject;
}

function createPageLoadingSpinner() {
    isLoading = true;
    const pageSpinner = document.createElement('div');
    pageSpinner.classList.add('search_page-spinner');
    pageSpinner.innerHTML = `
        <div class="search_page-spinner_inner">
            <svg viewBox="0 0 50 50">
                <circle cx="25" cy="25" r="20"></circle>
            </svg>
            <span>Lade Daten</span>
        </div>
        <div class="search_page-spinner_bg"></div>
    `;
    searchMainEl.appendChild(pageSpinner);
}

function removePageLoadingSpinner() {
    isLoading = false;
    const pageSpinner = searchMainEl.querySelector('.search_page-spinner');
    if (pageSpinner) pageSpinner.remove();
}

function preventClick(event) {
    event.preventDefault();
    event.stopPropagation();
}

function disableFilterButtons() {
    document.querySelectorAll('.filter-facets_facet-item').forEach((item) => {
        item.classList.add('disabled');
        item.addEventListener('click', preventClick, true);
    });
}

function enableFilterButtons() {
    document.querySelectorAll('.filter-facets_facet-item').forEach((item) => {
        item.classList.remove('disabled');
        item.removeEventListener('click', preventClick, true);
    });
}
